import * as XLSX from 'xlsx';

/**
 * Excel 題目解析器
 * 用於解析 xlsx/xls 格式的題目文件
 */
export class XlsxQuizParser {
  constructor() {
    this.questions = [];
    this.errors = [];
  }

  /**
   * 解析 xlsx 文件
   * @param {File} file - 文件對象
   * @returns {Promise<Array>} 解析後的題目數組
   */
  async parseFile(file) {
    const reader = new FileReader();

    return new Promise((resolve, reject) => {
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          if (workbook.SheetNames.length === 0) {
            throw new Error('xlsx 文件中沒有工作表');
          }

          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(firstSheet, {
            header: 1,
            defval: '',
            blankrows: false
          });

          if (rows.length < 2) {
            throw new Error('xlsx 文件中沒有數據行（至少需要標題行和1條數據）');
          }

          this.questions = this.parseRows(rows);

          if (this.questions.length === 0) {
            throw new Error('未找到有效題目');
          }

          resolve({
            questions: this.questions,
            errors: this.errors
          });
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error('文件讀取失敗'));
      };

      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * 解析行數據
   * @param {Array} rows - xlsx 行數據
   * @returns {Array} 題目數組
   */
  parseRows(rows) {
    const questions = [];
    const headers = rows[0];

    // 建立欄位索引映射
    const colMap = this.buildColumnIndexMap(headers);

    // 驗證必要欄位
    this.validateColumnMap(colMap);

    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];
      if (!row || row.length === 0) continue;

      try {
        const question = this.parseQuestion(row, colMap, i + 1);
        if (question) {
          questions.push(question);
        }
      } catch (error) {
        this.errors.push({
          row: i + 1,
          error: error.message
        });
        console.warn(`第 ${i + 1} 行解析失敗:`, error.message);
      }
    }

    return questions;
  }

  /**
   * 建立欄位索引映射
   * @param {Array} headers - 表頭數組
   * @returns {Object} 欄位映射
   */
  buildColumnIndexMap(headers) {
    const map = {};

    headers.forEach((header, index) => {
      const h = String(header).trim().toLowerCase();

      if (h.includes('itemindex') || h.includes('題號') || h === '#' || h === '分數') {
        map.itemIndex = index;
      }
      if (h.includes('question') || h.includes('題目') || h.includes('題幹')) {
        map.question = index;
      }
      if (h.includes('option1') || h.includes('選項1') || h.includes('答案選項1') || h.includes('答案選項 1')) {
        map.option1 = index;
      }
      if (h.includes('option2') || h.includes('選項2') || h.includes('答案選項2') || h.includes('答案選項 2')) {
        map.option2 = index;
      }
      if (h.includes('option3') || h.includes('選項3') || h.includes('答案選項3') || h.includes('答案選項 3')) {
        map.option3 = index;
      }
      if (h.includes('option4') || h.includes('選項4') || h.includes('答案選項4') || h.includes('答案選項 4')) {
        map.option4 = index;
      }
      if (h.includes('answer') || h.includes('答案')) {
        map.answer = index;
      }
      if (h.includes('point') || h.includes('配分') || h.includes('分數')) {
        map.point = index;
      }
      if (h.includes('type') || h.includes('題型')) {
        map.type = index;
      }
      if (h.includes('prompt') || h.includes('提示') || h.includes('評分標準')) {
        map.prompt = index;
      }
    });

    return map;
  }

  /**
   * 驗證必要欄位
   * @param {Object} colMap - 欄位映射
   */
  validateColumnMap(colMap) {
    const required = ['question'];
    const missing = [];

    required.forEach(field => {
      if (colMap[field] === undefined) {
        missing.push(field);
      }
    });

    if (missing.length > 0) {
      throw new Error(`缺少必要欄位: ${missing.join(', ')}`);
    }
  }

  /**
   * 將單選/多選題答案正規化成字母（A、B、C…）
   * 支援：字母（不分大小寫）、數字索引（1-based）、選項全文，
   * 多選可用逗號/頓號/空白分隔，或直接連續書寫（如 "13"、"AC"）。
   * @param {String} rawAnswer - 原始答案字串
   * @param {Array} options - 選項全文陣列
   * @returns {String|null} 正規化後的字母答案；無法對應時回傳 null
   */
  normalizeChoiceAnswer(rawAnswer, options) {
    const toLetter = (token) => {
      if (/^[A-Za-z]$/.test(token)) {
        const letter = token.toUpperCase();
        const idx = letter.charCodeAt(0) - 65;
        return idx < options.length ? letter : null;
      }
      if (/^\d+$/.test(token)) {
        const idx = parseInt(token, 10) - 1;
        return idx >= 0 && idx < options.length ? String.fromCharCode(65 + idx) : null;
      }
      const idx = options.findIndex(opt => opt === token);
      return idx !== -1 ? String.fromCharCode(65 + idx) : null;
    };

    // 先嘗試以逗號/頓號/空白分隔多個答案（供多選或選項全文比對使用）
    const tokens = String(rawAnswer)
      .split(/[,，、\s]+/)
      .map(t => t.trim())
      .filter(t => t !== '');

    if (tokens.length > 1) {
      const letters = tokens.map(toLetter);
      return letters.includes(null) ? null : letters.join('');
    }

    // 單一 token：先當整體比對（字母、數字、選項全文）
    const singleLetter = toLetter(tokens[0]);
    if (singleLetter !== null) {
      return singleLetter;
    }

    // 無分隔符的連續多選答案（如 "13"、"AC"）：逐字元拆解
    if (tokens[0].length > 1) {
      const letters = tokens[0].split('').map(toLetter);
      if (!letters.includes(null)) {
        return letters.join('');
      }
    }

    return null;
  }

  /**
   * 解析單個題目
   * @param {Array} row - 行數據
   * @param {Object} colMap - 欄位映射
   * @param {Number} rowNumber - 行號
   * @returns {Object} 題目對象
   */
  parseQuestion(row, colMap, rowNumber) {
    const questionText = row[colMap.question];

    // 數據驗證
    if (!questionText || String(questionText).trim() === '') {
      throw new Error(`第 ${rowNumber} 行：題目內容為空`);
    }

    // 收集選項
    const options = [];
    [colMap.option1, colMap.option2, colMap.option3, colMap.option4].forEach(idx => {
      if (idx !== undefined) {
        const optionText = row[idx];
        if (optionText && String(optionText).trim() !== '') {
          options.push(String(optionText).trim());
        }
      }
    });

    // 解析答案
    let answer = '';
    if (colMap.answer !== undefined) {
      const rawAnswer = row[colMap.answer];
      if (rawAnswer !== undefined && rawAnswer !== '') {
        answer = String(rawAnswer).trim();

        // 是非題答案轉換：「是」→「A」，「非/不是」→「B」
        if (answer === '是' || answer.toLowerCase() === 'true' || answer.toLowerCase() === 't' || answer === '對' || answer === 'o') {
          answer = 'A';
        } else if (answer === '非' || answer === '不是' || answer === '否' || answer.toLowerCase() === 'false' || answer.toLowerCase() === 'f' || answer === '錯' || answer === 'x') {
          answer = 'B';
        } else if (options.length > 0) {
          // 單選/多選題：把數字索引或選項全文正規化成對應字母（A、B、C…），
          // 確保與 IRS 實際送回的字母答案格式一致，避免課堂互動分恆為 0。
          const normalized = this.normalizeChoiceAnswer(answer, options);
          if (normalized !== null) {
            answer = normalized;
          } else {
            this.errors.push({
              row: rowNumber,
              error: `答案「${answer}」無法對應到選項，請確認答案欄位填寫字母（如 C）、數字索引（如 3）或選項全文`
            });
          }
        }
      }
    }

    // 解析配分
    let point = 10; // 默認配分
    if (colMap.point !== undefined) {
      const rawPoint = row[colMap.point];
      if (rawPoint !== undefined && rawPoint !== '') {
        const parsedPoint = parseInt(rawPoint);
        if (!isNaN(parsedPoint) && parsedPoint > 0) {
          point = parsedPoint;
        }
      }
    }

    // 解析題型
    let type = 'single'; // 默認單選題
    if (colMap.type !== undefined) {
      const rawType = row[colMap.type];
      if (rawType && String(rawType).trim() !== '') {
        const typeStr = String(rawType).trim().toLowerCase();
        if (typeStr === 'single' || typeStr === '單選') {
          type = 'single';
        } else if (typeStr === 'judge' || typeStr === '判斷' || typeStr === 'true/false' || typeStr === 'tf') {
          type = 'judge';
        } else if (typeStr === 'multiple' || typeStr === '多選') {
          type = 'multiple';
        } else if (typeStr === 'complete' || typeStr === '填空') {
          type = 'complete';
        } else if (typeStr === 'subjective' || typeStr === '問答' || typeStr === '主觀') {
          type = 'subjective';
        } else {
          // 未知題型，保持原值以便顯示「不支援」
          type = typeStr;
        }
      }
    }

    // 自動推斷題型
    if (options.length === 0) {
      type = answer ? 'complete' : 'subjective';
    }

    // 解析提示
    let prompt = '';
    if (colMap.prompt !== undefined) {
      const rawPrompt = row[colMap.prompt];
      if (rawPrompt && String(rawPrompt).trim() !== '') {
        prompt = String(rawPrompt).trim();
      }
    }

    // 解析題號
    let itemIndex = rowNumber - 1;
    if (colMap.itemIndex !== undefined) {
      const rawIndex = row[colMap.itemIndex];
      if (rawIndex !== undefined && rawIndex !== '') {
        const parsedIndex = parseInt(rawIndex);
        if (!isNaN(parsedIndex) && parsedIndex > 0) {
          itemIndex = parsedIndex;
        }
      }
    }

    return {
      itemIndex,
      type,
      question: String(questionText).trim(),
      options,
      answer,
      point,
      prompt
    };
  }
}
